import React, { useEffect, useMemo } from "react";
import { navigate } from "gatsby";
import { getPriceToShow, getPriceValueToShow } from "../../../../../utils/currency";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../../../../store";
import { addToCart } from "../../../../../store/order";
import Button from "../../../../shared/Button";
import EcommerceApi from "../../../../../services/ecommerceApi";
import { useSalesChannel } from "../../../../../context/SalesChannelContext";
import { jsonParseString } from "../../../../../utils/string";
import { ProductDataType } from "../../../../../types";
import { ProductJsonLd } from "gatsby-plugin-next-seo";
import { GatsbyImage } from "gatsby-plugin-image";

const ProductCard = ({ product }) => {
    const dispatch = useDispatch<AppDispatch>();

    const salesChannel = useSalesChannel();
    const currencyCode = useSelector(
        (state: RootState) => state.currencyCode.selectedCurrencyCode?.currencyCode
    );
    const [adding, setAdding] = React.useState(false);
    const [productData, setproductData] = React.useState<ProductDataType | null>(null);

    const store = useSelector((state: RootState) => state);
    const { order: orderStore } = store;

    const hasPage = product?.url ? true : false;

    useEffect(() => {
        loadProductData();
    }, []);

    const handleLinkClick = e => {
        e.preventDefault();

        if (hasPage) {
            navigate(product?.url);
        }
    };

    const loadProductData = async () => {
        if (product) {
            const productId = product.product || product.id;
            const [productData] = await Promise.all([
                EcommerceApi.getProductData(productId)
            ]);
            setproductData(productData || null);
        }
    };

    const handleButtonClick = async () => {
        setAdding(true);
        const orderId =
            orderStore?.order && orderStore?.order ? (orderStore?.order as any).id : null;
        const params = {
            orderId: orderId,
            skuId: product.id,
            quantity: 1,
            salesChannel,
            currencyCode
        };
        await dispatch(addToCart(params));
        setAdding(false);
    };

    const priceToShow = useMemo(() => {
        return {
            hidden: !productData?.currentPrice,
            price: getPriceToShow(
                productData?.currentPrice || 0,
                salesChannel,
                currencyCode,
                undefined,
                undefined,
                true,
                0
            ),
            priceValue: getPriceValueToShow(
                productData?.currentPrice || 0,
                salesChannel
            )
        }
    }, [currencyCode, productData?.currentPrice]);

    const selectedSchema = useMemo(() => {
        if (product?.schema) {
            try {
                const parsed = JSON.parse(product.schema);

                // if not array
                if (!Array.isArray(parsed)) {
                    return parsed;
                }

                // if array
                const found = parsed.find(item => item.salesChannelId === salesChannel.id);
                return found;

            } catch (e) {
                console.warn(e);
            }
        }
        return null;
    }, [product]);

    const reviewsSchema: any[] = useMemo(() => {
        return (product?.reviews || []).map(rev => {

            const firstName = rev.author.split(" ")[0];
            const lastName = rev.author.split(" ")[1];

            return {
                "@type": "Review",
                author: {
                    "@type": "Person",
                    name: rev.author,
                    firstName,
                    lastName
                },
                datePublished: rev.datePublished,
                reviewBody: rev.body,
                name: rev.title,
                reviewRating: {
                    bestRating: "5",
                    ratingValue: rev.ratingValue,
                    worstRating: "0"
                }
            };
        });
    }, [product.reviews]);

    const shortDescription = useMemo(() => {

        const json = jsonParseString(product.shortDescription);

        if (json && salesChannel.id) {
            const arr = json || [];
            const scData = arr.find(item => item.salesChannelId === salesChannel.id);
            if (scData) {
                return scData.shortDescription;
            }
        }

        return product.shortDescription;
    }, [product.shortDescription]);

    const html = useMemo(() => {

        const json = jsonParseString(product.productMetricHtml);

        if (json && salesChannel.id) {
            const arr = json || [];
            const scData = arr.find(item => item.salesChannelId === salesChannel.id);
            if (scData) {
                return scData.productMetricHtml;
            }
        }

        return product.productMetricHtml;
    }, [product.productMetricHtml]);

    const RYALOGO = useMemo(() => {
        return product.assets?.find(asset => asset.name === "RYA_LOGO");
    }, [product.assets]);


    return (
        <div className="pt-2 p-5 bg-white border-t-[5px] border-b-[5px] border-primary h-full flex flex-col items-between text-center">
            <div className="text-center">
                {RYALOGO && (
                    <div className="flex justify-center mt-2">
                        <GatsbyImage className="rounded-full mb-4 max-w-[130px]" image={RYALOGO.imageFile?.childImageSharp.gatsbyImageData} alt={product.shortName} />
                    </div>
                )}
                <h3 className="text-[22px]">{product.shortName}</h3>
                <div className="py-6" style={{
                    opacity: priceToShow.hidden ? 0 : 1
                }} dangerouslySetInnerHTML={{ __html: priceToShow.price }}></div>
                <div className="pb-6">
                    <span className="text-2xl">{shortDescription}</span>
                    {html && (
                        <div className="mt-3" dangerouslySetInnerHTML={{ __html: html }}></div>
                    )}
                </div>
            </div>

            <div className="grow flex flex-col justify-end">
                <div className="flex justify-center">
                    <Button
                        className="w-[150px]"
                        padding="py-2 px-3"
                        onClick={handleButtonClick}
                        disabled={adding}
                        loader={adding}
                    >
                        Sign Up
                    </Button>
                </div>
                <div className={`${!hasPage && "opacity-0"} flex justify-center mt-2`}>
                    <Button
                        disabled={!hasPage}
                        className="w-[180px]"
                        type="underline"
                        padding="py-2 px-3"
                        onClick={handleLinkClick}
                    >
                        Find out More
                    </Button>
                </div>
            </div>

            <ProductJsonLd
                name={product?.SKUName}
                brand={selectedSchema?.brand}
                images={product.imageFile?.publicURL ? [product.imageFile?.publicURL] : undefined}
                gtin={selectedSchema?.gtin}
                offers={!!priceToShow ? {
                    price: priceToShow.priceValue.toFixed(2),
                    priceCurrency: currencyCode || "GBP",
                    availability: product.isTracked === false ? "InStock" : undefined,
                } : undefined}
                description={selectedSchema?.description || product.shortDescription}
                mpn={selectedSchema?.mpn}
                aggregateRating={salesChannel.aggregateReview?.ratingValue ? {
                    ratingValue: salesChannel.aggregateReview?.ratingValue,
                    reviewCount: salesChannel.aggregateReview?.count
                } : undefined}
                overrides={{
                    "@type": "Product",
                    reviews: reviewsSchema
                }}
            />

        </div>
    );
};

export default ProductCard;
